import { vars } from "@vericus/cadmus-ui";

import {
  CriterionFragment,
  CriterionOutcomeFragment,
  GroupPeerOutcomeFragment,
  PeerOverviewQuery,
} from "@/generated/graphql";

export const MEMBERS_COLUMN_LABEL = "Group members";
export const STATUS_COLUMN_LABEL = "Status";

export enum RubricColumns {
  STATUS = "status",
  STUDENT = "student",
}

export const EMPTY_SCORE_PLACEHOLDER = "--";

/**
 * Calculates the pending peer evaluations for a student work group.
 *
 * @returns An object containing:
 *   - `pendingMarkIds`: An array of user IDs that have pending evaluations.
 *   - `totalPendingEvaluations`: The total number of pending evaluations (number
 *     of students * number of pending criteria).
 */
export function getPendingPeerEvaluations(
  data: PeerOverviewQuery,
  selfEvaluationEnabled: boolean
): {
  pendingMarkIds: string[];
  totalPendingEvaluations: number;
  actualTotalEvaluations: number;
} {
  const { me, studentWorkGroup, peerRubric, groupPeerOutcomes } = data;

  if (!studentWorkGroup?.group?.members || !peerRubric) {
    return {
      pendingMarkIds: [],
      totalPendingEvaluations: 0,
      actualTotalEvaluations: 0,
    };
  }

  const groupMembers = selfEvaluationEnabled
    ? studentWorkGroup.group.members
    : studentWorkGroup.group.members.filter(
        (member) => member.id !== data.me.id
      );

  if (!groupPeerOutcomes) {
    return {
      pendingMarkIds: groupMembers.map((member) => member.id),
      totalPendingEvaluations: groupMembers.length * peerRubric.criteria.length,
      actualTotalEvaluations: 0,
    };
  }

  const criteria = peerRubric.criteria;

  const expectedCriteriaCount = criteria.length;
  const expectedTotalEvaluations = groupMembers.length * expectedCriteriaCount;

  // Group evaluations by target user
  const outcomesByTarget = new Map<string, number>();

  const givenCriterionOutcomes = groupPeerOutcomes.flatMap(
    (outcome) => outcome.givenCriterionOutcomes
  );

  const studentPeerOutcomeGivenProcessed = selfEvaluationEnabled
    ? givenCriterionOutcomes
    : givenCriterionOutcomes.filter(
        (outcome) => outcome.targetUserId !== me.id
      );

  for (const outcome of studentPeerOutcomeGivenProcessed) {
    const count = outcomesByTarget.get(outcome.targetUserId ?? "") ?? 0;

    if (outcome.markerRubricScaleItemId) {
      outcomesByTarget.set(outcome.targetUserId ?? "", count + 1);
    }
  }

  const pendingMarkIds = groupMembers
    .filter((member) => {
      const completed = outcomesByTarget.get(member.id) ?? 0;
      return completed < expectedCriteriaCount;
    })
    .map((member) => member.id);

  const actualTotalEvaluations = studentPeerOutcomeGivenProcessed.length;

  const totalPendingEvaluations =
    expectedTotalEvaluations - actualTotalEvaluations;

  return {
    pendingMarkIds,
    totalPendingEvaluations,
    actualTotalEvaluations,
  };
}

export function getPeerSpecialString(selfPeerId: string, studentId: string) {
  if (selfPeerId === studentId) {
    return " (Self Eval)";
  }
  return "";
}

function buildScaleMap(
  rubricCriteria: CriterionFragment[]
): Map<string, string> {
  const scaleMap = new Map<string, string>();
  rubricCriteria.forEach((criterion) => {
    criterion.scaleItems.forEach((item) => {
      scaleMap.set(item.id ?? "", item.scaleLabel ?? "");
    });
  });
  return scaleMap;
}

function buildOutcomeLookup(
  outcomes: CriterionOutcomeFragment[]
): Map<string, Map<string, CriterionOutcomeFragment>> {
  const outcomeLookup = new Map<
    string,
    Map<string, CriterionOutcomeFragment>
  >();
  for (const outcome of outcomes) {
    const targetUserId = outcome.targetUserId ?? "";
    const criterionId = outcome.markerRubricCriterionId;

    if (!outcomeLookup.has(targetUserId)) {
      outcomeLookup.set(targetUserId, new Map());
    }

    outcomeLookup.get(targetUserId)!.set(criterionId, outcome);
  }
  return outcomeLookup;
}

function getFilteredMembers(
  groupMembers: { id: string; name: string }[],
  selfId: string,
  selfEvaluationEnabled: boolean
): { id: string; name: string }[] {
  const members = groupMembers.filter((m) => m.id !== selfId);
  if (selfEvaluationEnabled) {
    const self = groupMembers.find((m) => m.id === selfId);
    if (self) {
      members.push(self);
    }
  }
  return members;
}

function buildCriteriaMap(
  rubricCriteria: CriterionFragment[]
): { id: string; title: string }[] {
  return rubricCriteria.map((c) => ({
    id: c.id ?? "",
    title: c.title ?? "",
  }));
}

export interface PeerOutcomeRow {
  userId: string;
  userName: string;
  outcomes: {
    outcomeId: string;
    criteriaId: string;
    criteriaValue: string;
    scaleId: string;
    scaleValue: string;
  }[];
}

export type PeerOutcomeTable = PeerOutcomeRow[];

export function transformPeerOutcomesTable(
  groupPeerOutcomes: GroupPeerOutcomeFragment[],
  rubricCriteria: CriterionFragment[],
  groupMembers: { id: string; name: string }[],
  selfId: string,
  selfEvaluationEnabled: boolean
): {
  peerOutcomeTable: PeerOutcomeTable;
  criteriaMap: { id: string; title: string }[];
} {
  const studentPeerOutcomesGiven = groupPeerOutcomes
    ? groupPeerOutcomes.flatMap((outcome) => outcome.givenCriterionOutcomes)
    : [];

  const scaleMap = buildScaleMap(rubricCriteria);
  const outcomeLookup = buildOutcomeLookup(studentPeerOutcomesGiven);
  const filteredMembers = getFilteredMembers(
    groupMembers,
    selfId,
    selfEvaluationEnabled
  );

  const criteriaMap = buildCriteriaMap(rubricCriteria);
  const peerOutcomeTable = filteredMembers.map((member) => {
    const outcomes = rubricCriteria.map((criterion) => {
      const match =
        outcomeLookup.get(member.id)?.get(criterion.id ?? "") ?? null;

      const scaleId = match?.markerRubricScaleItemId ?? null;
      const scaleValue =
        scaleId !== null
          ? (scaleMap.get(scaleId) ?? EMPTY_SCORE_PLACEHOLDER)
          : EMPTY_SCORE_PLACEHOLDER;

      return {
        outcomeId: match?.id ?? "",
        criteriaId: criterion.id ?? "",
        criteriaValue: criterion.skill?.skill ?? criterion.title ?? "",
        scaleId: scaleId ?? "",
        scaleValue: scaleValue,
      };
    });

    return {
      userId: member.id,
      userName: member.name,
      outcomes,
    };
  });

  return {
    peerOutcomeTable,
    criteriaMap,
  };
}

export enum WorkStatus {
  InProgress = "inProgress",
  GroupFormationInProgress = "groupFormationInProgress",
  DraftSubmitted = "draftSubmitted",
  FinalSubmitted = "finalSubmitted",
  FeedbackReleased = "feedbackReleased",
  Overdue = "overdue",
  NotStarted = "notStarted",
}

export const pillColors: Record<
  WorkStatus,
  { backgroundColor: string; textColor: string }
> = {
  [WorkStatus.InProgress]: {
    backgroundColor: vars.colors.lilac200,
    textColor: vars.colors.lilac700,
  },
  [WorkStatus.GroupFormationInProgress]: {
    backgroundColor: "#FDF8E2",
    textColor: vars.colors.yellow800,
  },
  [WorkStatus.DraftSubmitted]: {
    backgroundColor: vars.colors.linkBg,
    textColor: vars.colors.navy500,
  },
  [WorkStatus.FinalSubmitted]: {
    backgroundColor: vars.colors.lilac200,
    textColor: vars.colors.lilac700,
  },
  [WorkStatus.FeedbackReleased]: {
    backgroundColor: vars.colors.lilac200,
    textColor: vars.colors.lilac700,
  },
  [WorkStatus.Overdue]: { backgroundColor: "#F6E4ED", textColor: "#660044" },
  [WorkStatus.NotStarted]: {
    backgroundColor: vars.colors.bgrey300,
    textColor: vars.colors.navy400,
  },
};

export function getWorkStatus(
  isGroupEssay: boolean,
  workId: string | null,
  finalised: boolean,
  resultId: string | undefined,
  dueDate: string | null | undefined
): { status: WorkStatus; label?: string } {
  const finalDate = dueDate ? new Date(dueDate).getTime() : null;
  const isDateOverdue = finalDate && Date.now() > finalDate;

  const isGroupFormationInProgress = isGroupEssay && workId && !finalised;
  const isFinalSubmitted = workId && !!resultId;

  const isInProgress = workId && !resultId;

  if (isFinalSubmitted) {
    return { status: WorkStatus.FinalSubmitted, label: "Final Submitted" };
  }

  if (isGroupFormationInProgress) {
    return {
      status: WorkStatus.GroupFormationInProgress,
      label: "Group formation in progress",
    };
  }

  if (isInProgress) {
    if (isDateOverdue) {
      return { status: WorkStatus.Overdue, label: "Overdue" };
    }

    return { status: WorkStatus.InProgress, label: "In Progress" };
  }

  return { status: WorkStatus.NotStarted, label: "Not yet started" };
}
